import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiX, FiCreditCard, FiCalendar, FiTrendingUp, FiDollarSign, FiEye, FiDownload } from 'react-icons/fi';
import axiosConfig from '../../../api/axiosConfig';
import * as XLSX from 'xlsx';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

const PaymentDetailsModal = ({ isOpen, onClose }) => {
  const adminId = localStorage.getItem('adminId');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedDateRange, setSelectedDateRange] = useState({
    start: '',
    end: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [bankFilter, setBankFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Debounced search
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  
  // Stats
  const [stats, setStats] = useState({
    payment_count: 0,
    total_amount: 0,
    average_amount: 0,
    today_amount: 0,
    week_amount: 0,
    month_amount: 0
  });
  
  // Banka listesi
  const [bankList, setBankList] = useState([]);

  // Ödemeleri yükle
  const fetchPayments = useCallback(async () => {
    setLoading(true);
    
    try {
      const params = {
        period: selectedPeriod,
        year: selectedYear,
        month: selectedMonth,
        bank: bankFilter,
        search: debouncedSearchTerm
      };
      
      // Custom date range için
      if (selectedPeriod === 'custom' && selectedDateRange.start && selectedDateRange.end) {
        params.start_date = selectedDateRange.start;
        params.end_date = selectedDateRange.end;
      }
      
      const response = await axiosConfig.get('/admin/odemes', { params });
      
      if (response.data) {
        setPayments(response.data.data || []);
        setStats(response.data.stats || {});
        
        // Tüm banka listesini al
        const allBanks = response.data.banks || [];
        setBankList(allBanks);
      }
    } catch (error) {
      console.error('Ödemeler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, selectedYear, selectedMonth, selectedDateRange, bankFilter, debouncedSearchTerm]);

  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      fetchPayments();
    }
  }, [isOpen, fetchPayments]);

  // Sıralanmış ve sayfalanmış ödemeler
  const sortedAndPaginatedPayments = useMemo(() => {
    const sorted = [...payments];
    
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.tarih) - new Date(a.tarih);
        case 'date_asc':
          return new Date(a.tarih) - new Date(b.tarih);
        case 'amount_desc':
          return (b.tutar || 0) - (a.tutar || 0);
        case 'amount_asc':
          return (a.tutar || 0) - (b.tutar || 0);
        case 'name_asc':
          return (a.student_name || '').localeCompare(b.student_name || '');
        case 'name_desc':
          return (b.student_name || '').localeCompare(a.student_name || '');
        default:
          return 0;
      }
    });
    
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    
    return sorted.slice(startIndex, endIndex);
  }, [payments, sortBy, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(payments.length / itemsPerPage);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const exportToExcel = () => {
    try {
      const excelData = payments.map(payment => ({
        'Tarih': formatDate(payment.tarih),
        'Saat': new Date(payment.tarih).toLocaleTimeString('tr-TR', { 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        'Öğrenci Adı': payment.student_name || '-',
        'Öğrenci Email': payment.student_email || '-',
        'Veli Adı': payment.parent_name || '-',
        'Veli Telefon': payment.parent_phone || '-',
        'Banka': payment.banka_ismi || 'Belirtilmemiş',
        'Tutar (TL)': payment.tutar || 0
      }));

      const summaryData = [
        { 'Tarih': '', 'Saat': '', 'Öğrenci Adı': '', 'Öğrenci Email': '', 'Veli Adı': '', 'Veli Telefon': '', 'Banka': '', 'Tutar (TL)': '' },
        { 'Tarih': 'ÖZET BİLGİLER', 'Saat': '', 'Öğrenci Adı': '', 'Öğrenci Email': '', 'Veli Adı': '', 'Veli Telefon': '', 'Banka': '', 'Tutar (TL)': '' },
        { 'Tarih': 'Toplam Ödeme Sayısı:', 'Saat': payments.length, 'Öğrenci Adı': '', 'Öğrenci Email': '', 'Veli Adı': '', 'Veli Telefon': '', 'Banka': '', 'Tutar (TL)': '' },
        { 'Tarih': 'Toplam Tutar:', 'Saat': '', 'Öğrenci Adı': '', 'Öğrenci Email': '', 'Veli Adı': '', 'Veli Telefon': '', 'Banka': '', 'Tutar (TL)': stats.total_amount },
        { 'Tarih': 'Ortalama Tutar:', 'Saat': '', 'Öğrenci Adı': '', 'Öğrenci Email': '', 'Veli Adı': '', 'Veli Telefon': '', 'Banka': '', 'Tutar (TL)': stats.average_amount }
      ];

      const finalData = [...excelData, ...summaryData];
      const ws = XLSX.utils.json_to_sheet(finalData);

      const colWidths = [
        { wch: 12 }, // Tarih
        { wch: 10 }, // Saat
        { wch: 25 }, // Öğrenci Adı
        { wch: 30 }, // Öğrenci Email
        { wch: 25 }, // Veli Adı
        { wch: 15 }, // Veli Telefon
        { wch: 20 }, // Banka
        { wch: 15 }  // Tutar
      ];
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Gelen Ödemeler');

      const date = new Date();
      const fileName = `Gelen_Odemeler_${date.getFullYear()}_${String(date.getMonth() + 1).padStart(2, '0')}_${String(date.getDate()).padStart(2, '0')}.xlsx`;

      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Excel export hatası:', error);
    }
  };

  const getDateRangeText = () => {
    const today = new Date();
    if (selectedPeriod === 'today') {
      return today.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    } else if (selectedPeriod === 'thisWeek') {
      const startOfWeek = new Date(today);
      const dayOfWeek = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      return `${startOfWeek.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} - ${endOfWeek.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (selectedPeriod === 'thisMonth') {
      return today.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
    } else if (selectedPeriod === 'thisYear') {
      return today.getFullYear() + ' Yılı';
    } else if (selectedPeriod === 'all') {
      return 'Tüm Zamanlar';
    } else if (selectedPeriod === 'custom' && selectedDateRange.start && selectedDateRange.end) {
      return `${formatDate(selectedDateRange.start)} - ${formatDate(selectedDateRange.end)}`;
    }
    return 'Özel Tarih Aralığı';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <FiCreditCard className="mr-2 text-green-600" />
              Alınan Ödeme Detayları
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {getDateRangeText()} - {stats.payment_count} ödeme
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportToExcel}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <FiDownload className="text-lg" />
              Excel
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <FiX className="text-2xl text-gray-600" />
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 p-4 border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setSelectedPeriod('today')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedPeriod === 'today'
                ? 'bg-green-600 text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            Bugün
          </button>
          <button
            onClick={() => setSelectedPeriod('thisWeek')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedPeriod === 'thisWeek'
                ? 'bg-green-600 text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            Bu Hafta
          </button>
          <button
            onClick={() => setSelectedPeriod('thisMonth')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedPeriod === 'thisMonth'
                ? 'bg-green-600 text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            Bu Ay
          </button>
          <button
            onClick={() => setSelectedPeriod('thisYear')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedPeriod === 'thisYear'
                ? 'bg-green-600 text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            Bu Yıl
          </button>
          <button
            onClick={() => setSelectedPeriod('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedPeriod === 'all'
                ? 'bg-green-600 text-white'
                : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
            }`}
          >
            Tümü
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex flex-col">
              <p className="text-xs text-gray-500">Toplam Tahsilat</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(stats.total_amount)}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex flex-col">
              <p className="text-xs text-gray-500">Ödeme Sayısı</p>
              <p className="text-lg font-bold text-gray-800">{stats.payment_count}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex flex-col">
              <p className="text-xs text-gray-500">Ortalama</p>
              <p className="text-lg font-bold text-purple-600">{formatCurrency(stats.average_amount)}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex flex-col">
              <p className="text-xs text-gray-500">Bugün</p>
              <p className="text-lg font-bold text-orange-600">{formatCurrency(stats.today_amount)}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex flex-col">
              <p className="text-xs text-gray-500">Bu Hafta</p>
              <p className="text-lg font-bold text-indigo-600">{formatCurrency(stats.week_amount)}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 shadow-sm">
            <div className="flex flex-col">
              <p className="text-xs text-gray-500">Bu Ay</p>
              <p className="text-lg font-bold text-teal-600">{formatCurrency(stats.month_amount)}</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Öğrenci ara..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              value={bankFilter}
              onChange={(e) => setBankFilter(e.target.value)}
            >
              <option value="all">Tüm Bankalar</option>
              {bankList.map(bank => (
                <option key={bank} value={bank}>{bank}</option>
              ))}
            </select>
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date_desc">En Yeni</option>
              <option value="date_asc">En Eski</option>
              <option value="amount_desc">En Yüksek</option>
              <option value="amount_asc">En Düşük</option>
              <option value="name_asc">A-Z İsim</option>
              <option value="name_desc">Z-A İsim</option>
            </select>
          </div>
        </div>

        {/* Pagination Top */}
        {payments.length > itemsPerPage && (
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Toplam <span className="font-semibold">{payments.length}</span> ödeme
              <span className="ml-2">(
                {((currentPage - 1) * itemsPerPage) + 1} - 
                {Math.min(currentPage * itemsPerPage, payments.length)} arası gösteriliyor
              )</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Önceki
              </button>
              <span className="px-3 py-1 text-sm">
                Sayfa {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sonraki →
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : sortedAndPaginatedPayments.length === 0 ? (
            <div className="text-center py-12">
              <FiCreditCard className="text-5xl text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Bu döneme ait ödeme kaydı bulunamadı.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="sticky top-0 bg-white border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Tarih</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Öğrenci</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Veli</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Banka</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-700">Tutar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedAndPaginatedPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-gray-800">{formatDate(payment.tarih)}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(payment.tarih).toLocaleTimeString('tr-TR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium">{payment.student_name || '-'}</p>
                        <p className="text-sm text-gray-500">{payment.student_email || '-'}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm text-gray-800">{payment.parent_name || '-'}</p>
                        <p className="text-sm text-gray-500">{payment.parent_phone || '-'}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {payment.banka_ismi || 'Belirtilmemiş'}
                      </span>
                    </td>
                    <td className="text-right py-3 px-4">
                      <p className="font-semibold text-green-600 text-lg">
                        {formatCurrency(payment.tutar)}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-gray-300 bg-gray-50">
                <tr>
                  <td colSpan="4" className="py-4 px-4 font-bold text-gray-700">
                    TOPLAM ({sortedAndPaginatedPayments.length} ödeme)
                  </td>
                  <td className="text-right py-4 px-4 font-bold text-green-700 text-lg">
                    {formatCurrency(sortedAndPaginatedPayments.reduce((sum, p) => sum + parseFloat(p.tutar || 0), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 p-4 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {payments.length > itemsPerPage ? (
              <span>
                Sayfa {currentPage} / {totalPages} - 
                Toplam <span className="font-semibold">{payments.length}</span> ödeme
              </span>
            ) : (
              <span>Toplam <span className="font-semibold">{payments.length}</span> ödeme</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailsModal;
